import type { Glossary } from './glossary.schema'

const glossaryIT: Glossary = {
  algorithm: {
    id: 'algorithm',
    term: 'Algoritmo decompressivo',
    synonyms: ['modello decompressivo'],
    category: 'algorithm',
    level: 'basic',
    short_definition: 'Modello matematico che calcola come il gas inerte si carica e si scarica dai tessuti durante un\'immersione, indicando quando e dove fermarsi in risalita.',
    full_explanation: `Un **algoritmo decompressivo** è il cuore di ogni computer subacqueo. È un modello matematico che simula come l'azoto (e l'elio, se usato) entrano ed escono dai tessuti del corpo durante e dopo un'immersione.

L'obiettivo è calcolare la **risalita più sicura possibile**, evitando la formazione di bolle di gas che possono causare la **malattia da decompressione (MDD)**.

Esistono due famiglie principali:

- **Modelli a gas disciolto** (Bühlmann ZH-L16, USN, DCIEM): assumono che le bolle non si formino se la tensione del gas resta sotto soglie tabulate.
- **Modelli a bolle** (VPM-B, RGBM): simulano direttamente la nucleazione e la crescita delle bolle.

La scelta dell'algoritmo e dei suoi parametri influenza profondità e durata degli stop di decompressione, ma **nessun algoritmo è infallibile**. La sicurezza dipende anche da idratazione, fitness, temperatura e ripetitivi recenti.`,
    related_terms: ['buhlmann_zhl16', 'vpm', 'gradient_factor'],
    references: [
      { title: 'Decompression — Decompression Sickness', citation: 'A.A. Bühlmann, 1995' },
    ],
  },

  buhlmann_zhl16: {
    id: 'buhlmann_zhl16',
    term: 'Bühlmann ZH-L16',
    category: 'algorithm',
    level: 'intermediate',
    short_definition: 'Famiglia di algoritmi decompressivi a 16 compartimenti, sviluppata dal prof. A.A. Bühlmann (Università di Zurigo) tra gli anni \'60 e \'90.',
    full_explanation: `Il **modello Bühlmann ZH-L16** divide teoricamente il corpo in 16 compartimenti tissutali, ognuno con un proprio **half-time** (tempo di dimezzamento) che va da 4 minuti (compartimento più rapido, simile al sangue) a 635 minuti (compartimento più lento, simile alle cartilagini).

Durante l'immersione, il modello calcola minuto per minuto quanto azoto (ed eventualmente elio) si è caricato in ciascun compartimento. Ogni compartimento ha un **M-value**, ovvero la massima tensione di gas che può tollerare a una data profondità senza causare bolle sintomatiche.

Esistono tre varianti:

- **ZH-L16A**: coefficienti puramente teorici, mai usati in pratica.
- **ZH-L16B**: per **tabelle di decompressione** stampate.
- **ZH-L16C**: per **computer subacquei real-time**, leggermente più conservativo. È il default praticamente universale.

Quasi tutti i computer moderni (Shearwater, Suunto, Garmin) usano ZH-L16C, spesso combinato con i **Gradient Factors** per personalizzare la conservatività.`,
    related_terms: ['gradient_factor', 'm_value', 'compartment', 'half_time'],
    references: [
      { title: 'Decompression — Decompression Sickness', citation: 'A.A. Bühlmann, 1995' },
    ],
  },

  gradient_factor: {
    id: 'gradient_factor',
    term: 'Gradient Factor',
    synonyms: ['GF'],
    category: 'algorithm',
    level: 'intermediate',
    short_definition: 'Frazione dell\'M-value tollerata, configurabile per personalizzare la conservatività della decompressione.',
    full_explanation: `I **Gradient Factors (GF)** sono un meccanismo introdotto da Erik Baker negli anni 2000 per rendere l'algoritmo di Bühlmann più conservativo in modo controllato.

Invece di permettere alla tensione del gas inerte nei tessuti di raggiungere il 100% dell'M-value (il limite teorico di Bühlmann), si limita a una **frazione percentuale**. Esempio: GF = 80% significa che si tollera fino all'80% dell'M-value.

Il sistema usa **due valori**:

- **GF Low**: applicato al primo stop di decompressione (più profondo). Determina **dove** iniziano gli stop.
- **GF High**: applicato all'arrivo in superficie. Determina la **conservatività finale**.

Tra i due punti, il GF cresce **linearmente** con la riduzione della profondità.

**Esempi di setting**:

- 90/95: aggressivo, vicino a Bühlmann puro
- 45/85: moderato, default Shearwater
- 30/70: conservativo, usato in immersioni tecniche profonde`,
    worked_example: 'Un diver scende a 40m, sta 25 minuti, e usa GF 40/85. Il computer calcola il primo stop a circa 12m (più profondo che con GF 90/95) usando il GF Low di 40%. Mentre risale, il GF aumenta linearmente fino al 85% all\'arrivo in superficie, allungando lo stop a 3m rispetto a setting più aggressivi.',
    related_terms: ['m_value', 'buhlmann_zhl16', 'gf_low', 'gf_high', 'gf99'],
    references: [
      { title: 'Clearing Up The Confusion About "Deep Stops"', citation: 'Erik Baker, 1998' },
      { title: 'Decompression — Decompression Sickness', citation: 'A.A. Bühlmann, 1995' },
    ],
  },

  gf_low: {
    id: 'gf_low',
    term: 'GF Low',
    category: 'algorithm',
    level: 'intermediate',
    short_definition: 'Frazione dell\'M-value applicata al primo (più profondo) stop di decompressione. Determina dove iniziano gli stop.',
    full_explanation: `**GF Low** controlla la **profondità del primo stop** di decompressione.

- Un GF Low **basso** (es. 30) → primo stop più profondo. Maggiore protezione dei tessuti rapidi nei primi metri di risalita, al prezzo di un carico maggiore sui tessuti lenti.
- Un GF Low **alto** (es. 85) → primo stop più superficiale o assente. Risalita più diretta, profili più simili alle vecchie tabelle.

Il dibattito tra **deep stops** e **shallow stops** è uno dei più discussi della subacquea moderna. Studi NEDU del 2011 hanno suggerito che stop troppo profondi possano peggiorare il caricamento dei tessuti lenti, portando la comunità tecnica verso GF Low più alti (es. 50-70) rispetto al passato (30-40).`,
    related_terms: ['gf_high', 'gradient_factor', 'deep_stop'],
  },

  gf_high: {
    id: 'gf_high',
    term: 'GF High',
    category: 'algorithm',
    level: 'intermediate',
    short_definition: 'Frazione dell\'M-value applicata all\'arrivo in superficie. Determina il margine di sicurezza finale.',
    full_explanation: `**GF High** controlla **quanto puoi avvicinarti all'M-value** quando emergi dall'immersione.

- GF High = 100% → tolleri il 100% dell'M-value (Bühlmann puro, aggressivo)
- GF High = 70% → tolleri solo il 70%, lasciando un margine di sicurezza del 30%

Un GF High **basso** allunga gli stop poco profondi (3-6m), perché bisogna scaricare più gas prima di poter emergere. Questi stop superficiali proteggono soprattutto i **tessuti lenti**.

Valori tipici:

- 95: aggressivo (Bühlmann quasi puro)
- 85: default Shearwater, moderato
- 75-80: conservativo, raccomandato per tecnica
- 70: molto conservativo, raccomandato in caso di disidratazione, freddo, fatica, ripetitivi multipli`,
    related_terms: ['gf_low', 'gradient_factor', 'm_value'],
  },

  gf99: {
    id: 'gf99',
    term: 'GF99',
    category: 'algorithm',
    level: 'advanced',
    short_definition: 'Surfacing GF equivalente: se emergessi in questo istante, quale sarebbe il GF effettivo nel compartimento più caricato?',
    full_explanation: `**GF99** è un valore in tempo reale che rappresenta **quanto sei vicino al limite** se emergessi immediatamente.

È calcolato come: \`(tensione_attuale - p_ambiente) / (M_value_a_superficie - p_ambiente) × 100\`

Esempi:

- GF99 = 30%: sei molto sotto il limite, ampio margine
- GF99 = 85%: sei al limite del tuo GF High se questo è 85%
- GF99 = 100%: hai esattamente l'M-value pieno; oltre è violazione

Durante una decompressione corretta, il GF99 oscilla salendo durante le risalite tra stop e scendendo durante gli stop stessi. Visualizzarlo nel tempo permette di vedere quanto stress decompressivo abbia avuto un'immersione.`,
    related_terms: ['gradient_factor', 'm_value', 'leading_compartment'],
  },

  m_value: {
    id: 'm_value',
    term: 'M-value',
    category: 'algorithm',
    level: 'advanced',
    short_definition: 'Massima tensione di gas inerte che un compartimento può tollerare a una data pressione ambiente senza causare bolle sintomatiche.',
    full_explanation: `L'**M-value** ("Maximum value") è il fulcro del modello di Bühlmann. È la **soglia di tolleranza** specifica di ciascuno dei 16 compartimenti.

Formula:

\`M = a + (P_ambiente / b)\`

Dove \`a\` (in bar) e \`b\` (adimensionale) sono coefficienti tabulati specifici per ogni compartimento e ogni gas (N₂ ed He hanno coefficienti separati). I compartimenti **rapidi** hanno M-value più alti (più tolleranti), i **lenti** hanno M-value più bassi (più sensibili).

In pratica: se la tensione di gas in un compartimento supera il suo M-value, l'algoritmo prevede formazione di bolle e impone uno stop. Il punto in cui un compartimento è esattamente al suo M-value è il **ceiling**.`,
    related_terms: ['compartment', 'ceiling', 'gradient_factor', 'buhlmann_zhl16'],
  },

  compartment: {
    id: 'compartment',
    term: 'Compartimento',
    synonyms: ['tessuto teorico'],
    category: 'physiology',
    level: 'basic',
    short_definition: 'Modello matematico di un tipo di tessuto del corpo, caratterizzato da una specifica velocità di carico/scarico del gas inerte.',
    full_explanation: `Un **compartimento** non corrisponde a un tessuto reale, ma è un'**astrazione matematica** che rappresenta un comportamento. Bühlmann ne usa 16, ognuno con un **half-time** diverso:

- **Compartimenti rapidi (1-5)**: 4-27 minuti. Rappresentano tessuti molto perfusi: sangue, midollo, sistema nervoso. Si caricano e scaricano velocemente.
- **Compartimenti medi (6-10)**: 38-146 minuti. Tessuti come muscoli e organi.
- **Compartimenti lenti (11-16)**: 187-635 minuti. Cartilagini, tessuto adiposo, articolazioni. Si saturano lentamente ma trattengono gas a lungo dopo l'immersione.

In una stessa immersione, **diversi compartimenti diventano critici in momenti diversi**: i rapidi durante la discesa e i primi minuti, i lenti durante immersioni lunghe e ripetitive.`,
    related_terms: ['half_time', 'm_value', 'leading_compartment', 'buhlmann_zhl16'],
  },

  half_time: {
    id: 'half_time',
    term: 'Half-time',
    synonyms: ['τ', 'tau', 'tempo di emisaturazione'],
    category: 'physiology',
    level: 'intermediate',
    short_definition: 'Tempo necessario perché un compartimento dimezzi la differenza tra la tensione del gas inerte attuale e quella ambientale.',
    full_explanation: `L'**half-time** (τ) è la costante di tempo che governa il carico/scarico di un compartimento. Esempio: se l'half-time è 8 minuti e all'inizio la differenza tra ambiente e tessuto è 4 bar, dopo 8 min la differenza è 2 bar, dopo 16 min è 1 bar, dopo 24 min è 0.5 bar, e così via.

Dopo **6 half-time** (~94% saturato/desaturato) il compartimento è considerato in equilibrio.

Gli half-time sono tabulati per:

- **Azoto (N₂)**: da 4 a 635 minuti
- **Elio (He)**: da 1.51 a 240 minuti (~2.65× più rapidi dell'N₂, perché l'He è più piccolo)

Questa differenza è il motivo per cui i mix con elio richiedono profili decompressivi specifici.`,
    related_terms: ['compartment', 'buhlmann_zhl16'],
  },

  leading_compartment: {
    id: 'leading_compartment',
    term: 'Leading compartment',
    synonyms: ['compartimento limitante'],
    category: 'algorithm',
    level: 'advanced',
    short_definition: 'Il compartimento che, in un dato istante, ha il ceiling più profondo e quindi limita la risalita.',
    full_explanation: `Il **leading compartment** (compartimento "in testa") è quello che impone il vincolo decompressivo in quel momento.

Cambia durante l'immersione:

- **All'inizio della risalita**: i compartimenti **rapidi** (1-3) sono leading. Sono saturi e impongono il primo stop.
- **Durante la decompressione**: man mano che i rapidi si scaricano, i **medi** (5-8) prendono il testimone.
- **Negli ultimi stop superficiali**: i **lenti** (10-14) diventano leading se l'immersione è stata lunga.

Visualizzare quale compartimento è leading momento per momento dà un'idea di **dove sta il vero rischio** in quella specifica immersione: profili brevi e profondi caricano i rapidi, profili lunghi e moderati caricano i lenti.`,
    related_terms: ['compartment', 'ceiling', 'gf99'],
  },

  ceiling: {
    id: 'ceiling',
    term: 'Ceiling',
    synonyms: ['soffitto'],
    category: 'safety',
    level: 'basic',
    short_definition: 'Profondità minima a cui puoi salire in sicurezza in un dato istante. Risalire sopra il ceiling è una violazione decompressiva.',
    full_explanation: `Il **ceiling** ("soffitto") è la profondità minima sicura calcolata dall'algoritmo in tempo reale. È il punto in cui il **leading compartment** raggiunge esattamente il suo M-value (con eventuale GF applicato).

Se risali sopra il ceiling, l'algoritmo prevede che la tensione di gas nel tessuto leading superi la soglia, generando bolle.

In computer ben configurati, il ceiling è mostrato in tempo reale come **profondità target dello stop attuale**. Esempio: se il ceiling è 8.4m, devi rimanere a profondità ≥ 9m (lo stop si fa in step di 3m, arrotondando per eccesso).

Una **ceiling violation** registrata dal computer è un evento serio: significa che hai risalito sopra il limite calcolato, anche per pochi secondi. Tutti i computer la segnalano e la maggior parte la registra nel log.`,
    related_terms: ['leading_compartment', 'm_value', 'decompression_stop'],
  },

  ndl: {
    id: 'ndl',
    term: 'NDL',
    synonyms: ['No-Decompression Limit', 'limite di non-decompressione'],
    category: 'safety',
    level: 'basic',
    short_definition: 'Tempo massimo che puoi rimanere a una data profondità senza richiedere stop di decompressione obbligatori in risalita.',
    full_explanation: `Il **NDL (No-Decompression Limit)** è il tempo che ti rimane prima di entrare in **decompressione obbligatoria**. Più sei profondo, più il NDL si riduce velocemente.

Esempi tipici (Bühlmann ZH-L16C, GF 45/85, aria):

- 18m: ~56 min
- 24m: ~30 min
- 30m: ~17 min
- 36m: ~10 min
- 40m: ~7 min

Quando il NDL arriva a 0, l'immersione diventa una **dive in deco** e devi effettuare gli stop obbligatori prima di emergere.

Le **immersioni ricreative** sono per definizione condotte entro il NDL, con un margine di sicurezza (safety stop di 3 minuti a 5m raccomandato anche se non obbligatorio).`,
    related_terms: ['decompression_stop', 'safety_stop', 'ceiling'],
  },

  tts: {
    id: 'tts',
    term: 'TTS',
    synonyms: ['Time-to-Surface', 'tempo alla superficie'],
    category: 'safety',
    level: 'intermediate',
    short_definition: 'Tempo totale dalla profondità attuale alla superficie, inclusi tutti gli stop obbligatori.',
    full_explanation: `Il **TTS (Time-to-Surface)** è una stima dinamica del tempo necessario per emergere in sicurezza dall'istante in cui inizi la risalita.

Include:

- Tempo di risalita tra ogni stop (a velocità configurata, tipicamente 9 m/min)
- Durata di tutti gli stop di decompressione
- Eventuale safety stop

È una metrica chiave nella **gestione del gas**: se il tuo TTS è 25 minuti e hai gas per 20, hai un problema. Computer tecnici mostrano il **GTR (Gas Time Remaining)** insieme al TTS per dare il margine di sicurezza in tempo reale.

Il TTS varia continuamente durante l'immersione: aumenta finché stai sul fondo (carichi tessuti), si stabilizza durante gli stop, scende man mano che desaturi.`,
    related_terms: ['decompression_stop', 'ceiling', 'ndl'],
  },

  decompression_stop: {
    id: 'decompression_stop',
    term: 'Stop di decompressione',
    category: 'safety',
    level: 'basic',
    short_definition: 'Sosta a profondità prefissata durante la risalita per permettere ai tessuti di scaricare il gas inerte in modo sicuro.',
    full_explanation: `Uno **stop di decompressione** (o "deco stop") è una sosta **obbligatoria** prevista dall'algoritmo durante la risalita. Avviene tipicamente a profondità multiple di 3 metri (es. 21m, 18m, 15m, 12m, 9m, 6m, 3m).

Durante lo stop:

- I tessuti scaricano gas verso il sangue
- Il sangue scarica gas verso i polmoni
- I polmoni espirano il gas

La durata di ogni stop è calcolata dall'algoritmo: ci si ferma finché la tensione del leading compartment non scende abbastanza da permettere la prosecuzione della risalita.

Distinzione importante:

- **Stop obbligatorio (deco)**: richiesto dall'algoritmo, saltarlo viola la decompressione
- **Safety stop**: raccomandato (3 min a 5m) anche in immersioni NDL come margine di sicurezza`,
    related_terms: ['safety_stop', 'ceiling', 'tts', 'ndl'],
  },

  safety_stop: {
    id: 'safety_stop',
    term: 'Safety stop',
    synonyms: ['sosta di sicurezza'],
    category: 'safety',
    level: 'basic',
    short_definition: 'Sosta raccomandata di 3 minuti a 5 metri al termine di ogni immersione, anche se non richiesta dall\'algoritmo.',
    full_explanation: `Il **safety stop** è una **sosta non obbligatoria** ma fortemente raccomandata da tutte le agenzie didattiche al termine di ogni immersione.

**Standard**: 3 minuti a 5 metri (alcuni preferiscono 5 minuti).

Funzione:

- Permette ulteriore scarico di gas dai tessuti rapidi
- Riduce il rischio di MDD subclinica
- Stabilizza la pressione prima dell'emersione

A differenza dello stop di decompressione, **non c'è penalità algoritmica** se viene saltato — ma il rischio di problemi aumenta. Tutti i computer subacquei moderni lo suggeriscono al termine di ogni dive.`,
    related_terms: ['decompression_stop', 'ndl'],
  },

  deep_stop: {
    id: 'deep_stop',
    term: 'Deep stop',
    category: 'algorithm',
    level: 'advanced',
    short_definition: 'Stop intermedio a profondità maggiore del primo stop richiesto dall\'algoritmo classico, introdotto per ridurre il carico sui tessuti rapidi.',
    full_explanation: `I **deep stops** sono soste effettuate **a profondità più grandi** rispetto a quelle previste dall'algoritmo Bühlmann puro.

L'idea, popolarizzata da Richard Pyle negli anni '90 e formalizzata nei modelli a bolle (VPM-B), era che fermarsi profondamente impedisse la formazione iniziale di bolle nei tessuti rapidi.

In Bühlmann + GF, si ottiene lo stesso effetto **abbassando GF Low**: con GF Low 30, il primo stop è molto più profondo che con GF Low 85.

**Controversia**: nel 2011 il **NEDU** (Navy Experimental Diving Unit) ha pubblicato uno studio che suggerisce che deep stops eccessivi possano **peggiorare il caricamento dei tessuti lenti**, aumentando il rischio di MDD. Da allora la comunità tecnica si è progressivamente spostata verso GF Low più alti (50-70) bilanciati con GF High più bassi (70-80).`,
    related_terms: ['gf_low', 'vpm', 'decompression_stop'],
    references: [
      { title: 'NEDU Deep Stops Study', citation: 'Naval Experimental Diving Unit, 2011' },
    ],
  },

  cns_percent: {
    id: 'cns_percent',
    term: 'CNS%',
    synonyms: ['percentuale CNS', 'tossicità centrale'],
    category: 'gas',
    level: 'intermediate',
    short_definition: 'Stima cumulativa della tossicità del sistema nervoso centrale dovuta all\'esposizione a ossigeno ad alta pressione parziale.',
    full_explanation: `Il **CNS% (Central Nervous System percentage)** è una stima del rischio di **convulsioni iperossiche** dovute a respirare ossigeno a pressione parziale elevata.

È basato sui limiti **NOAA**: per ogni livello di ppO₂, esiste un tempo massimo di esposizione tabulato. L'esposizione effettiva consuma una percentuale del limite.

Esempio: a ppO₂ 1.4 bar il limite NOAA è 150 minuti per esposizione singola. Se respiri quel mix per 30 min, hai accumulato 30/150 = 20% di CNS.

Limiti pratici:

- 80%: avvicinamento al limite di sicurezza
- 100%: rischio significativo, evitare
- > 100%: zona ad alto rischio

Il CNS% **decade** in superficie con un half-time di circa 90 minuti, quindi immersioni ripetitive con surface intervals brevi accumulano. Una **convulsione iperossica sott'acqua** è quasi sempre fatale per via dell'annegamento.`,
    related_terms: ['ppo2', 'otu', 'mod'],
  },

  otu: {
    id: 'otu',
    term: 'OTU',
    synonyms: ['Oxygen Tolerance Unit', 'Unità di tolleranza ossigeno'],
    category: 'gas',
    level: 'advanced',
    short_definition: 'Misura cumulativa della tossicità polmonare da ossigeno (effetti irritativi su lungo termine).',
    full_explanation: `Le **OTU (Oxygen Tolerance Units)** misurano la **tossicità polmonare** da ossigeno, distinta da quella centrale (CNS%).

Mentre il CNS% riguarda **convulsioni acute**, le OTU riguardano **infiammazione polmonare** dovuta a esposizioni prolungate a O₂ alto.

Formula (semplificata): \`OTU = t × ((ppO₂ - 0.5) / 0.5)^0.83\` per ppO₂ > 0.5 bar.

Limiti raccomandati:

- Single dive: 300 OTU
- 24h: 600 OTU
- Multiday (week): 2500 OTU totali

Gli OTU sono particolarmente rilevanti per sub commerciali e tecnici con esposizioni prolungate, e per pazienti in camera iperbarica terapeutica.`,
    related_terms: ['ppo2', 'cns_percent'],
  },

  ppo2: {
    id: 'ppo2',
    term: 'PpO₂',
    synonyms: ['pressione parziale di ossigeno', 'partial pressure of oxygen'],
    category: 'gas',
    level: 'intermediate',
    short_definition: 'Pressione parziale dell\'ossigeno nel mix respirato a una data profondità. Determina il rischio di tossicità iperossica.',
    full_explanation: `La **ppO₂** (pressione parziale di O₂) è il prodotto della frazione di O₂ nel mix per la pressione assoluta:

\`ppO₂ = F_O₂ × P_assoluta\`

Esempi (acqua salata, P = 1 bar/10m):

- Aria (21%) a 30m: 0.21 × 4.0 = **0.84 bar**
- Nitrox 32 a 30m: 0.32 × 4.0 = **1.28 bar**
- Nitrox 32 a 40m: 0.32 × 5.0 = **1.60 bar** ⚠️
- O₂ puro a 6m: 1.00 × 1.6 = **1.60 bar**

Limiti operativi:

- **1.4 bar**: massimo raccomandato per la fase **bottom**
- **1.6 bar**: massimo raccomandato per la fase **deco**

Superare 1.6 bar in fase attiva è considerato pericoloso per il rischio di convulsioni iperossiche improvvise.`,
    related_terms: ['mod', 'cns_percent', 'nitrox'],
  },

  mod: {
    id: 'mod',
    term: 'MOD',
    synonyms: ['Maximum Operating Depth', 'profondità massima operativa'],
    category: 'gas',
    level: 'intermediate',
    short_definition: 'Profondità massima a cui un dato gas può essere usato in sicurezza, definita dal limite di ppO₂ accettato.',
    full_explanation: `La **MOD (Maximum Operating Depth)** è la profondità massima a cui un gas è respirabile rispettando un limite di ppO₂ predefinito.

Formula:

\`MOD (m) = ((ppO₂_limite / F_O₂) - 1) × 10\`

Esempi (limite ppO₂ 1.4 bar):

- Aria (21%): MOD ≈ 56m
- Nitrox 32: MOD ≈ 33m
- Nitrox 36: MOD ≈ 28m
- Nitrox 50: MOD ≈ 18m

Per la **fase deco** si usa spesso ppO₂ 1.6 bar, allungando la MOD:

- Nitrox 50: MOD deco ≈ 22m
- O₂ puro: MOD deco ≈ 6m

Superare la MOD significa entrare in zona di rischio iperossico acuto.`,
    related_terms: ['ppo2', 'nitrox', 'cns_percent'],
  },

  surface_interval: {
    id: 'surface_interval',
    term: 'Surface interval',
    synonyms: ['intervallo di superficie', 'SI'],
    category: 'physiology',
    level: 'basic',
    short_definition: 'Tempo trascorso in superficie tra la fine di un\'immersione e l\'inizio della successiva.',
    full_explanation: `Il **surface interval** è il tempo che intercorre tra l'emersione e la successiva immersione. È critico perché determina **quanto gas residuo** hai nei tessuti quando inizi la dive successiva.

Durante il surface interval:

- I compartimenti **rapidi** (4-30 min HT) si scaricano in fretta: dopo 1-2 ore sono quasi puliti
- I compartimenti **lenti** (>200 min HT) richiedono molte ore: alcuni hanno bisogno di **24-72 ore** per il scarico completo

Un surface interval **breve** (es. 30 min) lascia tessuti significativamente caricati: il computer ne tiene conto e propone NDL ridotti per la dive successiva. Un SI **lungo** (>6h) avvicina i tessuti a uno stato pulito ma raramente li azzera del tutto.`,
    related_terms: ['repetitive_dive', 'compartment', 'no_fly_time'],
  },

  repetitive_dive: {
    id: 'repetitive_dive',
    term: 'Immersione ripetitiva',
    synonyms: ['repetitive dive', 'ripetitiva'],
    category: 'physiology',
    level: 'intermediate',
    short_definition: 'Immersione effettuata mentre i tessuti contengono ancora gas residuo dalla precedente, tipicamente entro 12-24 ore.',
    full_explanation: `Un'immersione è **ripetitiva** quando inizia con tessuti **non completamente puliti** dalla precedente. La definizione precisa varia: PADI considera ripetitiva ogni dive entro 6 ore, altri standard estendono fino a 12 o 24 ore.

Per un'immersione ripetitiva:

- Il NDL è **ridotto** rispetto a una dive iniziale
- Eventuali stop di decompressione sono **più lunghi**
- Il rischio di MDD è leggermente più alto

Regole di buon senso:

- Profondità massima della seconda < profondità massima della prima
- Mai più di 3-4 dive significative al giorno
- 24h di completo "riposo" almeno una volta a settimana in trip multi-day
- No-fly time prolungato (24h) dopo ripetitive`,
    related_terms: ['surface_interval', 'ndl', 'no_fly_time'],
  },

  no_fly_time: {
    id: 'no_fly_time',
    term: 'No-fly time',
    synonyms: ['tempo di non volo'],
    category: 'safety',
    level: 'basic',
    short_definition: 'Periodo di attesa in superficie dopo un\'immersione prima di poter prendere un volo, per evitare MDD da decompressione in cabina.',
    full_explanation: `Quando voli, la cabina dell'aereo è **pressurizzata a circa 2400-2500m di altitudine equivalente** (~0.75 bar invece di 1.013). Questa **riduzione di pressione** può estrarre gas residuo dai tessuti del subacqueo, causando bolle e MDD anche dopo un'immersione che sembrava sicura.

Linee guida **DAN (Divers Alert Network)**:

- **Single dive NDL**: aspetta almeno **12 ore** prima del volo
- **Multiple dive o multi-day**: aspetta almeno **18 ore**
- **Dive con decompressione**: aspetta almeno **24 ore**

Molti operatori usano **24 ore standard** per semplicità.

Il no-fly time vale anche per salire in alta montagna (>300m altitude change).`,
    related_terms: ['repetitive_dive', 'surface_interval', 'decompression_stop'],
    references: [
      { title: 'Flying After Diving Guidelines', citation: 'DAN (Divers Alert Network), 2002' },
    ],
  },

  vpm: {
    id: 'vpm',
    term: 'VPM',
    synonyms: ['Varying Permeability Model', 'modello a permeabilità variabile'],
    category: 'algorithm',
    level: 'advanced',
    short_definition: 'Famiglia di algoritmi decompressivi a bolle, alternativa ai modelli a gas disciolto come Bühlmann.',
    full_explanation: `Il **VPM (Varying Permeability Model)**, sviluppato da Yount e Hoffman a partire dagli anni '70, è un modello che simula direttamente la **formazione e crescita di bolle** nei tessuti, invece di assumere semplicemente soglie di tensione tabulate.

Idee chiave:

- Esistono **micro-nuclei** gassosi pre-esistenti nei tessuti, di vari raggi
- Sotto pressione (discesa) i nuclei vengono compressi
- In risalita possono crescere se la pressione esterna scende abbastanza
- L'algoritmo limita il **volume totale di gas libero** ammissibile

La versione moderna **VPM-B** introduce un meccanismo iterativo ("Critical Volume Algorithm") per profili lunghi.

**Pro**: stop più profondi e progressivi, distribuzione del carico più graduale.
**Contro**: meno validazione clinica rispetto a Bühlmann.

Molti computer (Shearwater, Subsurface) supportano sia Bühlmann GF che VPM-B.`,
    related_terms: ['buhlmann_zhl16', 'algorithm', 'deep_stop'],
    references: [
      { title: 'A Model for Predicting Diving Decompression Illness', citation: 'Yount & Hoffman, 1986' },
    ],
  },

  nitrox: {
    id: 'nitrox',
    term: 'Nitrox',
    synonyms: ['EAN', 'Enriched Air Nitrox'],
    category: 'gas',
    level: 'basic',
    short_definition: 'Miscela respiratoria di azoto e ossigeno con percentuale di O₂ superiore al 21% dell\'aria.',
    full_explanation: `Il **Nitrox** (anche detto **EAN** = Enriched Air Nitrox) è una miscela di N₂ e O₂ in cui l'ossigeno è **arricchito** rispetto all'aria.

Mix più comuni:

- **EAN32** (Nitrox 32): 32% O₂, 68% N₂
- **EAN36** (Nitrox 36): 36% O₂, 64% N₂
- **EAN50** (deco gas): 50% O₂, 50% N₂

Vantaggi:

- Meno azoto = meno carico decompressivo a parità di profondità e tempo
- NDL più estesi rispetto all'aria (es. a 24m: aria 30 min, EAN32 ~50 min)

Svantaggi:

- ppO₂ raggiunge il limite a profondità minore (vedi MOD)
- Bombole devono essere certificate "O₂ clean" se mix > 40%
- Costo maggiore

Per immersioni > 30m si usa spesso **aria sul fondo + Nitrox in decompressione**, ottimizzando sicurezza ed efficienza decompressiva.`,
    related_terms: ['ppo2', 'mod', 'cns_percent', 'ndl'],
  },
}

export default glossaryIT
